import { useSessionStorage } from '@vueuse/core'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * Composable para gerenciar configurações do modo embed
 *
 * Usa sessionStorage para persistir configurações durante a navegação,
 * eliminando a necessidade de passar query params em todas as rotas.
 *
 * @example
 * ```ts
 * const { isEmbedMode, locale, initFromQuery } = useEmbedConfig()
 *
 * onMounted(() => {
 *   initFromQuery(route)
 * })
 * ```
 */
export function useEmbedConfig() {
  // Persistência via sessionStorage (limpa ao fechar aba)
  const isEmbedMode = useSessionStorage('embed', false)
  const locale = useSessionStorage('locale', 'pt-BR')

  /**
   * Inicializa configurações a partir dos query params da rota
   * Deve ser chamado na primeira carga da aplicação
   *
   * @param route - Rota atual do Vue Router
   */
  function initFromQuery(route: RouteLocationNormalizedLoaded) {
    // Detecta modo embed
    isEmbedMode.value = route.query?.embed === 'true'

    // Detecta locale solicitado
    if (route.query.locale && typeof route.query.locale === 'string') {
      locale.value = route.query.locale
    }
  }

  /**
   * Reseta configurações para valores padrão
   * Útil para testes ou logout
   */
  function reset() {
    isEmbedMode.value = false
    locale.value = 'pt-BR'
  }

  /**
   * Verifica se o locale atual é diferente do padrão (pt-BR)
   */
  function needsTranslation() {
    return locale.value !== 'pt-BR' && locale.value !== 'pt'
  }

  return {
    isEmbedMode,
    locale,

    initFromQuery,
    reset,
    needsTranslation,
  }
}
